import { Autocomplete, Avatar, Box, FormControl, InputAdornment, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material"
import AddIcon from '@mui/icons-material/Add';
import { Controller, useFieldArray, useForm } from "react-hook-form";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { IMeal, IProduct } from "../types/types";
import { useSnackbar } from "./Snackbar";
import { ref, set, update } from "firebase/database";
import { realtimeDb } from "../utils/firebase";
import { useProducts } from "../hooks/useProducts";
import { Spinner } from "./spinner/Spinner";
import { useEffect, useState } from "react";
import { AddNewProductPopup } from "./AddNewProductPopup";
import SaveIcon from '@mui/icons-material/Save';
import { normalizePolish } from "../utils/normlizePolish";

export const MealForm = ({ editingMeal, onSuccess }: { editingMeal?: IMeal, onSuccess?: () => void }) => {
  const { register, handleSubmit, watch, control, setValue, getFieldState, reset } = useForm();
  const { fields, append, remove } = useFieldArray({ name: "ingredients", control });
  const { Snackbar, setSnackbarStatus } = useSnackbar();
  const { products, error, loading } = useProducts();
  const [open, setOpen] = useState(false);

  const onSubmit = async (meal: {}) => {
    setSnackbarStatus("working")
    const mappedIngredients = (meal as IMeal).ingredients.map((ingredient) => {
      const product = products.find((product) => product.name === ingredient.name)
      return {
        id: product?.id,
        quantity: product ? Number(ingredient.quantity) / Number(product?.capacity) : ingredient.quantity,
      }
    });
    const updatedMeal = { ...meal, ingredients: mappedIngredients } as IMeal;
    const mealId = normalizePolish(editingMeal ? editingMeal.id! : updatedMeal.name);

    try {
      const action = editingMeal ? update : set
      await action(ref(realtimeDb, `meals/${mealId}`), updatedMeal);
      setSnackbarStatus("done");
    } catch {

    } finally {
      reset();
      remove();
      onSuccess?.();
    }
    setTimeout(() => {
      setSnackbarStatus("idle");
    }, 2000)
  };

  useEffect(() => {
    if (editingMeal) {
      setValue("name", editingMeal.name);
      setValue("imageUrl", editingMeal.imageUrl);
      setValue("type", editingMeal.type);
      setValue("ingredients", editingMeal.ingredients.map((ing) => ({ name: ing.name, quantity: Number(ing.quantity * ing.capacity).toFixed(0) })));
    }
  }, [editingMeal, setValue]);

  if (loading) {
    <Spinner />
  }

  return (
    <>
      <form style={{ display: 'flex', flexDirection: 'column', gap: 16 }} onSubmit={handleSubmit(onSubmit)}>
        <TextField required label="Nazwa posiłku" variant="outlined" {...register("name")} slotProps={editingMeal ? { inputLabel: { shrink: true } } : undefined} />
        <TextField required label="Adres URL obrazka" variant="outlined" {...register("imageUrl")} slotProps={editingMeal ? { inputLabel: { shrink: true } } : undefined} />

        <FormControl>
          <InputLabel id="type-123-id">Rodzaj posiłku</InputLabel>
          <Controller control={control} {...register("type")} render={({field}) => {
            return (
              <Select id="type-123" labelId="type-123-id" required label="Rodzaj posiłku" value={field.value || ""} onChange={field.onChange} variant="outlined">
                <MenuItem value="breakfast">Śniadanie</MenuItem>
                <MenuItem value="lunch">Obiad</MenuItem>
                <MenuItem value="dinner">Kolacja</MenuItem>
                <MenuItem value="snack">Przekąska</MenuItem>
              </Select>
            )
          }} />
        </FormControl>
        <div>Składniki</div>
        {fields.map((field: { id: string, name?: string }, index) => {
          const selectedProduct = products.find(({ name }) => name === watch(`ingredients.${index}.name`))

          if (selectedProduct?.capacity && !getFieldState(`ingredients.${index}.quantity`).isDirty && !editingMeal) {
            setValue(`ingredients.${index}.quantity`, selectedProduct.capacity)
          }
          const noProducts = [{ name: "Nie udało się pobrać produktów", imageUrl: "", capacity: 1, quantity: 1, unit: "g" }] as IProduct[];
          return (
            <div style={{ display: 'flex', gap: 1 }} key={field.id}>
              <Autocomplete
                disablePortal
                options={error ? noProducts : products}
                sx={{ minWidth: 230, width: '100%' }}
                renderInput={(params) => <TextField required {...register(`ingredients.${index}.name`)} {...params} label="Wybierz składnik" />}
                getOptionLabel={(option) => option.name}
                noOptionsText={<div style={{ display: 'flex' }}><div>Nie znaleziono produktu, dodaj nowy kliakając w <AddIcon onClick={() => setOpen(true)} /></div></div>}
                onChange={(_event, product) => {
                  setValue(`ingredients.${index}.name`, product?.name)
                }}
                defaultValue={editingMeal ? editingMeal.ingredients[index] : undefined}
                getOptionDisabled={(option) => noProducts[0].name === option.name}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return error ? (
                    <Box key={key} value={option.name} sx={{ display: "flex", gap: 4, alignItems: "center" }} component="li" {...optionProps}>
                      <Typography>{option.name}</Typography>
                    </Box>
                  ) : (
                    <Box key={key} value={option.name} sx={{ display: "flex", gap: 4, alignItems: "center" }} component="li" {...optionProps}>
                      {option.imageUrl && <Avatar slotProps={{ root: { style: { background: "#fff" } }, img: { style: { objectFit: "contain" } } }} alt={option.name} src={option.imageUrl} variant="rounded" />}
                      <Typography>{option.name}</Typography>
                    </Box>
                  )
                }}

              />
              {selectedProduct && <TextField required label="Ilość" variant="outlined" {...register(`ingredients.${index}.quantity`)} slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">{selectedProduct.unit}</InputAdornment>,
                },
              }} />}
              <DeleteForeverIcon onClick={() => remove(index)} className="w-4 h-4" style={{ margin: 'auto', color: 'red' }} />
            </div>
          )
        })}
        <button
          type="button"
          onClick={() => append({ name: '', quantity: 1 })}
          className="flex items-center gap-1 bg-blue-600 text-white px-3 rounded-lg text-sm hover:bg-blue-700 transition-colors"
          style={{ paddingTop: 4, paddingBottom: 4, background: "blue", width: 160 }}
        >
          <AddIcon className="w-4 h-4" />
          Dodaj składnik
        </button>
        <button
          type="submit"
          className="m-auto flex items-center gap-1 bg-blue-600 text-white px-3 rounded-lg text-sm hover:bg-blue-700 transition-colors"
          style={{ paddingTop: 12, paddingBottom: 12, background: "#1f8038", marginTop: 16, display: "flex", justifyContent: "center" }}
        >
          {editingMeal ? <SaveIcon className="w-4 h-4" /> : <AddIcon className="w-4 h-4" />}
          {editingMeal ? "Zapisz" : "Dodaj posiłek"}
        </button>
      </form>
      <AddNewProductPopup open={open} setOpen={setOpen} />
      <Snackbar messages={{ done: editingMeal ? "Zaktualizowano posiłek" : "Dodano posiłek", working: "Zapisywanie" }} />
    </>
  )
}
